import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

const SUPPORTED_LANGUAGES = ['en_US', 'en_CA', 'fr_CA'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly storageKey = 'app-language';
  private readonly fallbackLanguage: SupportedLanguage = 'en_US';
  private readonly languageMessageType = 'EDA_SET_LANGUAGE';
  private languageMessageListenerAttached = false;

  private readonly translate = inject(TranslateService);
  private readonly platformId = inject(PLATFORM_ID);

  initializeLanguage(preferredLanguage?: string): Promise<void> {
    this.translate.addLangs([...SUPPORTED_LANGUAGES]);

    const languageFromUrl = this.getLanguageFromUrl();
    const browserLanguage = this.translate.getBrowserLang();
    const storedLanguage = this.getStoredLanguage();
    const languageToUse = this.resolveLanguage(
      languageFromUrl ?? preferredLanguage ?? storedLanguage ?? browserLanguage
    );

    this.listenForParentLanguageChanges();
    return this.useLanguage(languageToUse);
  }

  useLanguage(language: string): Promise<void> {
    const resolvedLanguage = this.resolveLanguage(language);
    const loadLanguage = firstValueFrom(this.translate.use(resolvedLanguage)).then(() => undefined);
    this.persistLanguage(resolvedLanguage);

    if (isPlatformBrowser(this.platformId)) {
      document.documentElement.lang = resolvedLanguage;
    }

    return loadLanguage;
  }

  getCurrentLanguage(): string {
    return this.translate.getCurrentLang() || this.translate.getFallbackLang() || this.fallbackLanguage;
  }

  private resolveLanguage(language?: string | null): SupportedLanguage {
    const normalizedLanguage = (language || '').trim().toLowerCase();

    if (normalizedLanguage === 'fr' || normalizedLanguage === 'fr-ca' || normalizedLanguage === 'fr_ca') {
      return 'fr_CA';
    }

    if (normalizedLanguage === 'en-ca' || normalizedLanguage === 'en_ca') {
      return 'en_CA';
    }

    if (
      normalizedLanguage === 'en-us' ||
      normalizedLanguage === 'en_us' ||
      normalizedLanguage === 'en' ||
      normalizedLanguage.startsWith('en-')
    ) {
      return 'en_US';
    }

    return this.fallbackLanguage;
  }

  private getStoredLanguage(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  private persistLanguage(language: SupportedLanguage): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.setItem(this.storageKey, language);
    } catch {
      // Storage unavailable (e.g., private mode, blocked by policy) — language preference not persisted.
    }
  }

  private getLanguageFromUrl(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const searchParams = new URLSearchParams(window.location.search);

    return searchParams.get('lang') || searchParams.get('language') || searchParams.get('locale');
  }

  private listenForParentLanguageChanges(): void {
    if (!isPlatformBrowser(this.platformId) || this.languageMessageListenerAttached) {
      return;
    }

    window.addEventListener('message', (event: MessageEvent) => {
      if (!this.isTrustedParentMessage(event)) {
        return;
      }

      const requestedLanguage = this.getLanguageFromMessage(event.data);
      if (!requestedLanguage) {
        return;
      }

      void this.useLanguage(requestedLanguage);
    });

    this.languageMessageListenerAttached = true;
  }

  private isTrustedParentMessage(event: MessageEvent): boolean {
    if (event.source !== window.parent) {
      return false;
    }

    const parentOrigin = this.getParentOrigin() ?? window.location.origin;
    return event.origin === parentOrigin;
  }

  private getParentOrigin(): string | null {
    if (!document.referrer) {
      return null;
    }

    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }

  private getLanguageFromMessage(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const message = payload as { type?: unknown; language?: unknown; lang?: unknown; locale?: unknown };

    if (message.type !== this.languageMessageType) {
      return null;
    }

    const languageValue = message.language ?? message.lang ?? message.locale;
    return typeof languageValue === 'string' ? languageValue : null;
  }
}
